import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import { isValidPrecinctForProduct, Precinct } from 'teacher-ui-client/utils/precincts'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { action } from '@ember/object'
import type { LessonDataEntry } from 'teacher-ui-client/contentful-client/entry-types'
import type InteractivePreviewService from 'teacher-ui-client/services/interactive-preview'
import {
  ReadingEggspressReadingLessonPreview,
  ReadingEggspressSpellingLessonPreview,
} from 'teacher-ui-client/interactive-preview/engines'
import { LessonsPage } from 'teacher-ui-client/components/page/lessons'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import { t } from 'ember-intl'
import { LessonsSearch } from 'teacher-ui-client/components/lessons/search'
import { LessonPreviewTile } from 'teacher-ui-client/components/tiles/lesson/preview'
import { PrecinctTabs } from 'teacher-ui-client/components/page-header/precinct-tabs'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { Banner } from 'teacher-ui-client/components/promo/banner'
import { featureFlag } from 'teacher-ui-client/helpers/feature-flag'
import bannerImage from '@images/banner_image.svg'
import type ReadingEggspressLessonsRoute from 'teacher-ui-client/subscription-type/lessons/reading-eggspress/previews/route'

interface Args {
  model: ModelFor<ReadingEggspressLessonsRoute>
}

export class ReadingEggspressPreviewsPage extends Component<Args> {
  @service router!: RouterService

  @service interactivePreview!: InteractivePreviewService

  @queryParam('subscription-type/lessons') precinct!: Precinct

  get precinctOrDefault() {
    return isValidPrecinctForProduct(Product.REX, this.precinct) ? this.precinct : Precinct.REX_READING
  }

  get precincts() {
    return [Precinct.REX_READING, Precinct.REX_SPELLING]
  }

  @action
  async setPrecinct(precinct: Precinct) {
    await this.router.transitionTo({
      queryParams: {
        precinct,
      },
    })
  }

  get lessonPreviewViewModels() {
    if (!this.args.model.loaderInstance?.isSuccessful) return []

    const entries = this.args.model.loaderInstance.value?.lessonsData.items ?? []
    const previews = this.args.model.loaderInstance.value?.previewByLessonDataId ?? {}
    const resources = this.args.model.loaderInstance.value?.resourcesByLessonDataId ?? {}

    return entries.map((entry) => {
      const lessonDataId = entry.sys.id
      const lessonData = entry.fields
      const lessonPreview = previews[lessonDataId]
      const lessonResources = resources[lessonDataId] ?? []
      return {
        id: entry.sys.id,
        title: lessonData.title,
        coverUrl: lessonPreview?.fields.cover?.fields.file?.url,
        openPreview: this.getOpenPreview(lessonData),
        lessonResources:
          lessonResources
            .map((resource) => {
              return {
                id: resource.sys.id,
                title: resource.fields.title,
                resourceType: resource.fields.resourceType,
                fileUrl: resource.fields.file?.fields.file?.url,
                videoUrl: resource.fields.videoUrl,
              }
            })
            .sort((a, b) => a.resourceType.localeCompare(b.resourceType)) ?? [],
        lessonData: {
          activityNumber: lessonData.activityNumber,
        },
      }
    })
  }

  getOpenPreview(lessonData: LessonDataEntry['fields']) {
    return () => {
      if (lessonData.precinct === Precinct.REX_READING) {
        this.interactivePreview.openPreview(
          new ReadingEggspressReadingLessonPreview(
            this,
            this.interactivePreview.curriculum,
            String(lessonData.activityNumber),
          ),
          { title: lessonData.title },
        )
      } else if (lessonData.precinct === Precinct.REX_SPELLING) {
        this.interactivePreview.openPreview(
          new ReadingEggspressSpellingLessonPreview(
            this,
            this.interactivePreview.curriculum,
            String(lessonData.activityNumber),
          ),
          { title: lessonData.title },
        )
      }
    }
  }

  <template>
    <LessonsPage
      @loaderInstance={{@model.loaderInstance}}
      @title={{t "pages.product.lessons.title" product=(t "products.reading-eggspress")}}
    >
      <:header>
        <PrecinctTabs
          class="mx-auto max-w-md"
          @themeKey={{ThemeKeys.blue}}
          @precincts={{this.precincts}}
          @precinct={{this.precinctOrDefault}}
          @changeTab={{this.setPrecinct}}
        />
        <LessonsSearch @themeKey={{ThemeKeys.blue}} />
      </:header>

      <:banner>
        {{#if (featureFlag "tui_rex_lessons_whats_new")}}
          <Banner
            @title="Exciting updates to Reading Eggspress are on the way!"
            @subtitle="We’re improving Reading Eggspress for your older learners, with revamped maps and lessons. Click below to find out more."
            @link="https://readingeggs.com.au/articles/new-reading-eggspress/"
            @linkText="Explore lessons and more"
            @image={{bannerImage}}
            @features={{Array
              "Curriculum-aligned skills"
              "Transferable strategies"
              "Diverse, levelled texts"
              "150+ new instruction videos"
            }}
          />
        {{/if}}
      </:banner>
      <:results>

        {{#each this.lessonPreviewViewModels as |viewModel|}}
          <LessonPreviewTile
            data-test-lesson-preview-entry={{viewModel.lessonData.activityNumber}}
            class="w-80"
            @entryId={{viewModel.id}}
            @title={{viewModel.title}}
            @coverUrl={{viewModel.coverUrl}}
            @coverClass="h-40"
            @lessonResources={{viewModel.lessonResources}}
            @openPreview={{viewModel.openPreview}}
          />
        {{else}}
          <NoResultsMessage data-test-empty />
        {{/each}}
        {{#if @model.loaderInstance.value.pageCount}}
          <BasePageSelector
            class="basis-full"
            @pageCount={{@model.loaderInstance.value.pageCount}}
            @itemCount={{@model.loaderInstance.value.lessonsData.total}}
          />
        {{/if}}
      </:results>
    </LessonsPage>
  </template>
}

export default RouteTemplate(ReadingEggspressPreviewsPage)
